import { Component, inject } from '@angular/core';
import { IonicModule, RadioGroupCustomEvent } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SettingsActions } from 'src/app/ngrx/settings/settings.actions';
import { settingsFeature } from 'src/app/ngrx/settings/settings.feature';
import { ILanguage } from 'src/app/ngrx/settings/settings.models';

@Component({
  selector: 'app-wizard-language',
  standalone: true,
  imports: [IonicModule, TranslatePipe],
  template: `
    <section class="intro ion-text-center">
      <div class="icon-wrap">
        <ion-icon name="language-outline"></ion-icon>
      </div>
      <h2>{{ 'settings.lang.title' | translate }}</h2>
      <p>{{ 'wizard.language.message' | translate }}</p>
    </section>

    <ion-radio-group class="language-list" [value]="language()" (ionChange)="choose($event)">
      @for (lang of languages; track lang) {
        <ion-item lines="none" [class.selected]="language() === lang.lang">
          <ion-radio labelPlacement="start" justify="space-between" [value]="lang.lang">
            {{ lang.translationKey | translate }}
          </ion-radio>
        </ion-item>
      }
    </ion-radio-group>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 20px;
      max-width: 520px;
      margin: 24px auto 0;
    }

    .intro {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .icon-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 104px;
      height: 104px;
      margin-bottom: 8px;
      border-radius: 32px;
      color: var(--ion-color-primary);
      background: rgba(var(--ion-color-primary-rgb), 0.12);
    }

    .icon-wrap ion-icon {
      font-size: 58px;
    }

    h2 {
      margin: 12px 0 8px;
      font-size: 1.55rem;
      font-weight: 700;
    }

    .intro p {
      margin: 0;
      color: var(--ion-color-step-600, #666);
      line-height: 1.5;
    }

    .language-list {
      display: grid;
      gap: 12px;
    }

    ion-item {
      --background: var(--ion-color-light);
      --border-radius: 16px;
      --inner-padding-end: 16px;
      --min-height: 60px;
      --padding-start: 16px;
      overflow: hidden;
      border: 1px solid var(--ion-color-step-150, #ddd);
      border-radius: 16px;
    }

    ion-item.selected {
      --background: rgba(var(--ion-color-primary-rgb), 0.08);
      border-color: rgba(var(--ion-color-primary-rgb), 0.45);
    }

    ion-radio {
      width: 100%;
      font-weight: 600;
    }

    @media (max-width: 380px) {
      .icon-wrap {
        width: 88px;
        height: 88px;
      }

      .icon-wrap ion-icon {
        font-size: 50px;
      }
    }
  `
})
export class LanguageStepComponent {
  private readonly store = inject(Store);
  public readonly translate = inject(TranslateService);
  public readonly language = this.store.selectSignal(settingsFeature.selectLanguage);


  public languages: { lang: string; translationKey: string }[] = this.translate.getLangs().map((value: string) => ({
    lang: value,
    translationKey: `settings.lang.${value}`
  }));

  public choose(event: RadioGroupCustomEvent<ILanguage>): void {
    this.store.dispatch(SettingsActions.changeLanguage({ language: event.detail.value }));
  }
}
